import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Mail, Send, Eye, Loader2, Plus, TrendingUp, Users, CheckCircle2, X, MousePointerClick, Code, FileText } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { toast } from './ui/sonner';
import { emailTemplates } from '../data/emailTemplates';
import { getAuthHeaders, checkTokenExpiration } from '../lib/auth';
import SentEmails from './SentEmails';

const Campaigns = () => {
  // Load active tab from localStorage, default to 'performance'
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('campaignsActiveTab');
    return savedTab || 'performance';
  });
  const [showNewEmail, setShowNewEmail] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [recipients, setRecipients] = useState('');
  const [customSubject, setCustomSubject] = useState('');
  const [contactName, setContactName] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [editMode, setEditMode] = useState('visual'); // 'visual' or 'html'
  const [editedContent, setEditedContent] = useState('');
  const contentEditableRef = useRef(null);
  const [campaignPerformance, setCampaignPerformance] = useState([]);
  const [loadingPerformance, setLoadingPerformance] = useState(true);
  const [sentEmailsRefresh, setSentEmailsRefresh] = useState(0);
  const [sentEmailsData, setSentEmailsData] = useState([]);

  // Persist active tab to localStorage when it changes
  useEffect(() => {
    localStorage.setItem('campaignsActiveTab', activeTab);
  }, [activeTab]);

  // Fetch campaign performance data
  useEffect(() => {
    const fetchPerformance = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
        const response = await fetch(`${apiUrl}/api/campaigns/performance`, {
          headers: getAuthHeaders(),
        });

        if (checkTokenExpiration(response)) {
          return;
        }

        const data = await response.json();

        if (data.success) {
          setCampaignPerformance(data.campaigns);
        }
      } catch (error) {
        console.error('Error fetching campaign performance:', error);
      } finally {
        setLoadingPerformance(false);
      }
    };

    fetchPerformance();
    // Refresh every 30 seconds
    const interval = setInterval(fetchPerformance, 30000);
    return () => clearInterval(interval);
  }, []);

  // Fetch sent emails data for per-email metrics
  useEffect(() => {
    const fetchSentEmails = async () => {
      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
        const response = await fetch(`${apiUrl}/api/campaigns/sent-emails?limit=1000`, {
          headers: getAuthHeaders(),
        });

        if (checkTokenExpiration(response)) {
          return;
        }

        const data = await response.json();
        if (data.success) {
          setSentEmailsData(data.emails);
        }
      } catch (error) {
        console.error('Error fetching sent emails:', error);
      }
    };

    fetchSentEmails();
    // Refresh when sentEmailsRefresh changes
    if (sentEmailsRefresh > 0) {
      fetchSentEmails();
    }
  }, [sentEmailsRefresh]);

  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setCustomSubject(template.subject);
    setContactName(''); // Reset contact name when template changes
  };

  const handleSendCampaign = async () => {
    if (!selectedTemplate) {
      toast.error('Please select an email template');
      return;
    }

    if (!recipients.trim()) {
      toast.error('Please enter recipient email addresses');
      return;
    }

    // Validate email addresses
    const emailList = recipients
      .split(',')
      .map(email => email.trim())
      .filter(email => email.length > 0);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emailList.filter(email => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      toast.error(`Invalid email addresses: ${invalidEmails.join(', ')}`);
      return;
    }

    setIsSending(true);

    // Validate contact name if required
    try {
      const apiUrl = import.meta.env.VITE_API_URL || 'https://api.axiosdevelopment.dev';
      const frontendUrl = import.meta.env.VITE_FRONTEND_URL || 'https://axiosdevelopment.dev';
      
      // Use edited content if available, otherwise use template HTML
      let htmlContent = editedContent 
        ? reconstructHtml(selectedTemplate.html, editedContent)
        : selectedTemplate.html;
      
      // Replace campaign link with correct domain and campaign ID
      if (selectedTemplate.campaignId) {
        htmlContent = htmlContent.replace(
          /(href=["'])([^"']*#contact\?c=[^"']+)(["'])/g,
          `$1${frontendUrl}/#contact?c=${selectedTemplate.campaignId}$3`
        );
      }
      
      // Replace contact name placeholder if template requires it
      if (selectedTemplate.requiresContactName) {
        if (contactName && contactName.trim()) {
          // If contact name is provided, replace with the name
          htmlContent = htmlContent.replace(/\{\{CONTACT_NAME\}\}/g, contactName.trim());
        } else {
          // If blank, remove the placeholder and adjust greeting
          // Handle "Hey, {{CONTACT_NAME}}!" format
          htmlContent = htmlContent.replace(/Hey,\s*\{\{CONTACT_NAME\}\}!/g, 'Hey!');
          // Handle "Hello {{CONTACT_NAME}}," format
          htmlContent = htmlContent.replace(/Hello\s*\{\{CONTACT_NAME\}\},/g, 'Hello,');
        }
      }
      
      const response = await fetch(`${apiUrl}/api/campaigns/send`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          templateId: selectedTemplate.id,
          campaignId: selectedTemplate.campaignId,
          recipients: emailList,
          subject: customSubject || selectedTemplate.subject,
          html: htmlContent,
        }),
      });

      if (checkTokenExpiration(response)) {
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to send campaign');
      }

      toast.success('Campaign sent successfully!', {
        description: `Emails sent to ${emailList.length} recipient(s)`,
      });

      // Reset form and close
      setRecipients('');
      setSelectedTemplate(null);
      setCustomSubject('');
      setContactName('');
      setPreviewMode(false);
      setEditMode('visual');
      setEditedContent('');
      setShowNewEmail(false);
      
      // Refresh performance data and switch to sent emails tab
      const perfResponse = await fetch(`${apiUrl}/api/campaigns/performance`, {
        headers: getAuthHeaders(),
      });
      const perfData = await perfResponse.json();
      if (perfData.success) {
        setCampaignPerformance(perfData.campaigns);
      }
      
      // Switch to sent emails tab to see the new email and refresh
      setActiveTab('sent-emails');
      setSentEmailsRefresh(prev => prev + 1);
    } catch (error) {
      console.error('Error sending campaign:', error);
      toast.error('Failed to send campaign', {
        description: error.message || 'Please try again later.',
      });
    } finally {
      setIsSending(false);
    }
  };

  // Helper function to extract main content from HTML
  const extractMainContent = (html) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      
      // Find all tr elements and check for the one that follows the "Main Content" comment
      const allTrs = doc.querySelectorAll('tr');
      
      for (let i = 0; i < allTrs.length; i++) {
        const tr = allTrs[i];
        // Check if previous sibling is a comment with "Main Content"
        let node = tr.previousSibling;
        while (node) {
          if (node.nodeType === 8 && node.textContent && node.textContent.includes('Main Content')) {
            const td = tr.querySelector('td.email-body');
            if (td) {
              return td.innerHTML;
            }
          }
          node = node.previousSibling;
        }
      }
      
      // Fallback: try to find td.email-body directly (in case comment structure is different)
      const emailBody = doc.querySelector('td.email-body');
      if (emailBody) {
        return emailBody.innerHTML;
      }
      
      return '';
    } catch (error) {
      console.error('Error extracting main content:', error);
      return '';
    }
  };

  // Helper function to reconstruct full HTML with edited content
  const reconstructHtml = (originalHtml, editedContent) => {
    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(originalHtml, 'text/html');
      
      // Find all tr elements and check for the one that follows the "Main Content" comment
      const allTrs = doc.querySelectorAll('tr');
      
      for (let i = 0; i < allTrs.length; i++) {
        const tr = allTrs[i];
        // Check if previous sibling is a comment with "Main Content"
        let node = tr.previousSibling;
        while (node) {
          if (node.nodeType === 8 && node.textContent && node.textContent.includes('Main Content')) {
            const td = tr.querySelector('td.email-body');
            if (td) {
              td.innerHTML = editedContent;
              // Serialize back to HTML string
              return doc.documentElement.outerHTML;
            }
          }
          node = node.previousSibling;
        }
      }
      
      // Fallback: try to find td.email-body directly
      const emailBody = doc.querySelector('td.email-body');
      if (emailBody) {
        emailBody.innerHTML = editedContent;
        return doc.documentElement.outerHTML;
      }
      
      return originalHtml;
    } catch (error) {
      console.error('Error reconstructing HTML:', error);
      return originalHtml;
    }
  };

  // Reset edited content when template changes
  useEffect(() => {
    if (selectedTemplate) {
      const mainContent = extractMainContent(selectedTemplate.html);
      setEditedContent(mainContent);
    }
  }, [selectedTemplate?.id]);

  // Sync content when switching between visual and HTML tabs
  useEffect(() => {
    if (!previewMode) return;
    
    // Small delay to ensure DOM is ready after tab switch
    const timeoutId = setTimeout(() => {
      if (editMode === 'visual' && contentEditableRef.current) {
        // When switching to visual, update contentEditable with current editedContent
        const currentContent = contentEditableRef.current.innerHTML;
        if (currentContent !== editedContent) {
          contentEditableRef.current.innerHTML = editedContent;
        }
      }
    }, 50);
    
    return () => clearTimeout(timeoutId);
  }, [editMode, previewMode]);
  
  // Update contentEditable when editedContent changes (but only if we're in visual mode)
  useEffect(() => {
    if (previewMode && editMode === 'visual' && contentEditableRef.current) {
      const currentContent = contentEditableRef.current.innerHTML;
      // Only update if content actually changed (avoid infinite loops)
      if (currentContent !== editedContent && editedContent) {
        contentEditableRef.current.innerHTML = editedContent;
      }
    }
  }, [editedContent, previewMode, editMode]);

  const handlePreview = () => {
    if (!selectedTemplate) {
      toast.error('Please select a template to preview');
      return;
    }
    // Ensure content is loaded when opening preview
    const mainContent = extractMainContent(selectedTemplate.html);
    setEditedContent(mainContent);
    if (contentEditableRef.current) {
      contentEditableRef.current.innerHTML = mainContent;
    }
    setPreviewMode(true);
  };

  const getCampaignStats = (campaignId) => {
    const visitStats = campaignPerformance.find(c => c.campaignId === campaignId) || {
      totalVisits: 0,
      totalConversions: 0,
      conversionRate: '0.00',
      lastVisit: null,
    };

    // Get email stats for this campaign
    const campaignEmails = sentEmailsData.filter(e => e.campaignId === campaignId);
    const totalEmails = campaignEmails.length;
    const totalOpens = campaignEmails.reduce((sum, e) => sum + (e.openCount || 0), 0);
    const totalClicks = campaignEmails.reduce((sum, e) => sum + (e.clickCount || 0), 0);
    const openRate = totalEmails > 0 ? ((totalOpens / totalEmails) * 100).toFixed(2) : '0.00';
    const clickRate = totalEmails > 0 ? ((totalClicks / totalEmails) * 100).toFixed(2) : '0.00';
    const clickToVisitRate = totalClicks > 0 ? ((visitStats.totalVisits / totalClicks) * 100).toFixed(2) : '0.00';

    return {
      ...visitStats,
      totalEmails,
      totalOpens,
      totalClicks,
      openRate,
      clickRate,
      clickToVisitRate,
      emails: campaignEmails,
    };
  };


  const getTemplateName = (campaignId) => {
    const template = emailTemplates.find(t => t.campaignId === campaignId);
    return template ? template.name : 'Unknown Campaign';
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2">Email Campaigns</h1>
          <p className="text-[#888680]">Track performance and send targeted emails to potential clients</p>
        </div>
        <Button
          onClick={() => setShowNewEmail(true)}
          className="bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#c4e005] font-semibold"
        >
          <Plus className="mr-2 h-4 w-4" />
          New Email
        </Button>
      </div>

      {/* Campaign Performance Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="border-[#3f4816] bg-[#302f2c]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-[#888680]">Total Campaigns</p>
              <Mail className="h-5 w-5 text-[#d9fb06]" />
            </div>
            <p className="text-3xl font-bold text-white">{emailTemplates.length}</p>
          </CardContent>
        </Card>

        <Card className="border-[#3f4816] bg-[#302f2c]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-[#888680]">Total Emails Sent</p>
              <Send className="h-5 w-5 text-[#d9fb06]" />
            </div>
            <p className="text-3xl font-bold text-white">
              {sentEmailsData.length}
            </p>
          </CardContent>
        </Card>

        <Card className="border-[#3f4816] bg-[#302f2c]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-[#888680]">Total Visits</p>
              <Users className="h-5 w-5 text-[#d9fb06]" />
            </div>
            <p className="text-3xl font-bold text-white">
              {loadingPerformance ? '...' : campaignPerformance.reduce((sum, c) => sum + c.totalVisits, 0)}
            </p>
          </CardContent>
        </Card>

        <Card className="border-[#3f4816] bg-[#302f2c]">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm text-[#888680]">Total Conversions</p>
              <CheckCircle2 className="h-5 w-5 text-[#d9fb06]" />
            </div>
            <p className="text-3xl font-bold text-white">
              {loadingPerformance ? '...' : campaignPerformance.reduce((sum, c) => sum + c.totalConversions, 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs for Performance and Sent Emails */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-[#1a1c1b] border border-[#3f4816]">
          <TabsTrigger 
            value="performance" 
            className="data-[state=active]:bg-[#3f4816] data-[state=active]:text-[#d9fb06] text-[#888680]"
          >
            Campaign Performance
          </TabsTrigger>
          <TabsTrigger 
            value="sent-emails"
            className="data-[state=active]:bg-[#3f4816] data-[state=active]:text-[#d9fb06] text-[#888680]"
          >
            Sent Emails
          </TabsTrigger>
        </TabsList>

        <TabsContent value="performance" className="mt-6">
          <Card className="border-[#3f4816] bg-[#302f2c]">
            <CardHeader>
              <CardTitle className="text-[#d9fb06]">Campaign Performance</CardTitle>
              <CardDescription className="text-[#888680]">
                Track visits and conversions for each campaign
              </CardDescription>
            </CardHeader>
            <CardContent>
          {loadingPerformance ? (
            <div className="text-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-[#d9fb06] mx-auto" />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#3f4816]">
                    <th className="text-left py-3 px-4 text-[#d9fb06] font-semibold">Campaign</th>
                    <th className="text-right py-3 px-4 text-[#d9fb06] font-semibold">Emails</th>
                    <th className="text-right py-3 px-4 text-[#d9fb06] font-semibold">Opens</th>
                    <th className="text-right py-3 px-4 text-[#d9fb06] font-semibold">Clicks</th>
                    <th className="text-right py-3 px-4 text-[#d9fb06] font-semibold">Visits</th>
                    <th className="text-right py-3 px-4 text-[#d9fb06] font-semibold">Conversions</th>
                    <th className="text-right py-3 px-4 text-[#d9fb06] font-semibold">Rate</th>
                  </tr>
                </thead>
                <tbody>
                  {emailTemplates.map((template) => {
                    const stats = getCampaignStats(template.campaignId);
                    return (
                      <tr key={template.id} className="border-b border-[#3f4816] hover:bg-[#1a1c1b]">
                        <td className="py-3 px-4">
                          <p className="text-white font-medium">{template.name}</p>
                        </td>
                        <td className="text-right py-3 px-4 text-white">{stats.totalEmails || 0}</td>
                        <td className="text-right py-3 px-4 text-white">
                          <div className="flex items-center justify-end space-x-1">
                            <Eye className="h-3 w-3 text-[#888680]" />
                            <span>{stats.totalOpens || 0}</span>
                            {stats.totalEmails > 0 && (
                              <span className="text-xs text-[#888680]">({stats.openRate}%)</span>
                            )}
                          </div>
                        </td>
                        <td className="text-right py-3 px-4 text-white">
                          <div className="flex items-center justify-end space-x-1">
                            <MousePointerClick className="h-3 w-3 text-[#888680]" />
                            <span>{stats.totalClicks || 0}</span>
                            {stats.totalEmails > 0 && (
                              <span className="text-xs text-[#888680]">({stats.clickRate}%)</span>
                            )}
                          </div>
                        </td>
                        <td className="text-right py-3 px-4 text-white">{stats.totalVisits}</td>
                        <td className="text-right py-3 px-4 text-white">{stats.totalConversions}</td>
                        <td className="text-right py-3 px-4">
                          <Badge className={stats.conversionRate > 0 ? 'bg-green-500/20 text-green-400 border-green-500/30' : 'bg-[#3f4816] text-[#888680] border-[#3f4816]'}>
                            {stats.conversionRate}%
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="sent-emails" className="mt-6">
          <SentEmails 
            refreshTrigger={sentEmailsRefresh}
          />
        </TabsContent>
      </Tabs>

      {/* New Email Dialog */}
      <Dialog open={showNewEmail} onOpenChange={setShowNewEmail}>
        <DialogContent className="bg-[#302f2c] border-[#3f4816] text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#d9fb06]">Create New Campaign</DialogTitle>
            <DialogDescription className="text-[#888680]">
              Choose a template and send targeted emails
            </DialogDescription>
          </DialogHeader>
          
          <div className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Template Selection */}
              <div className="lg:col-span-1 space-y-3">
                <Label className="text-[#d9fb06]">Select Template</Label>
                {emailTemplates.map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleTemplateSelect(template)}
                    className={`w-full text-left p-4 rounded-lg border transition-colors ${
                      selectedTemplate?.id === template.id
                        ? 'border-[#d9fb06] bg-[#3f4816]'
                        : 'border-[#3f4816] bg-[#1a1c1b] hover:border-[#d9fb06] hover:bg-[#3f4816]'
                    }`}
                  >
                    <div className="mb-2">
                      <h3 className="font-semibold text-white">{template.name}</h3>
                    </div>
                    <p className="text-sm text-[#888680]">{template.description}</p>
                  </button>
                ))}
              </div>

              {/* Campaign Form */}
              <div className="lg:col-span-2 space-y-4">
                {selectedTemplate ? (
                  <>
                    <div className="flex items-center justify-between">
                      <Label className="text-[#d9fb06]">Campaign Details</Label>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handlePreview}
                        className="border-[#3f4816] text-[#d9fb06] hover:bg-[#3f4816]"
                      >
                        <Eye className="mr-2 h-4 w-4" />
                        Preview
                      </Button>
                    </div>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="subject" className="text-[#d9fb06]">
                          Email Subject
                        </Label>
                        <Input
                          id="subject"
                          value={customSubject}
                          onChange={(e) => setCustomSubject(e.target.value)}
                          placeholder="Enter email subject"
                          className="bg-[#1a1c1b] border-[#3f4816] text-white placeholder:text-[#888680] focus:border-[#d9fb06]"
                        />
                      </div>

                      {selectedTemplate.requiresContactName && (
                        <div className="space-y-2">
                          <Label htmlFor="contactName" className="text-[#d9fb06]">
                            Contact Name <span className="text-[#888680]">(optional - will appear in greeting, e.g., "Hey, John!")</span>
                          </Label>
                          <Input
                            id="contactName"
                            value={contactName}
                            onChange={(e) => setContactName(e.target.value)}
                            placeholder="e.g. John Smith (leave blank for generic greeting)"
                            className="bg-[#1a1c1b] border-[#3f4816] text-white placeholder:text-[#888680] focus:border-[#d9fb06]"
                          />
                        </div>
                      )}

                      <div className="space-y-2">
                        <Label htmlFor="recipients" className="text-[#d9fb06]">
                          Recipients <span className="text-[#888680]">(comma-separated)</span>
                        </Label>
                        <Textarea
                          id="recipients"
                          value={recipients}
                          onChange={(e) => setRecipients(e.target.value)}
                          placeholder="client1@example.com, client2@example.com"
                          rows={4}
                          className="bg-[#1a1c1b] border-[#3f4816] text-white placeholder:text-[#888680] focus:border-[#d9fb06]"
                        />
                        <p className="text-xs text-[#888680]">
                          Enter email addresses separated by commas
                        </p>
                      </div>

                      <Button
                        onClick={handleSendCampaign}
                        disabled={isSending || !recipients.trim()}
                        className="w-full bg-[#d9fb06] text-[#1a1c1b] hover:bg-[#c4e005] font-semibold"
                      >
                        {isSending ? (
                          <>
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Send className="mr-2 h-4 w-4" />
                            Send Campaign
                          </>
                        )}
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <Mail className="h-12 w-12 text-[#888680] mx-auto mb-3" />
                    <p className="text-[#888680]">Select a template to configure your campaign</p>
                  </div>
                )}
              </div>
            </div>

            {/* Template Preview & Editor */}
            {previewMode && selectedTemplate && (
              <div className="mt-6 pt-6 border-t border-[#3f4816]">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-[#d9fb06] font-semibold">Email Preview & Editor</h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPreviewMode(false)}
                    className="text-[#888680] hover:text-[#d9fb06]"
                  >
                    Close
                  </Button>
                </div>
                
                <Tabs 
                  value={editMode} 
                  onValueChange={(value) => {
                    setEditMode(value);
                    // When switching to visual, ensure contentEditable is synced
                    if (value === 'visual' && contentEditableRef.current && editedContent) {
                      setTimeout(() => {
                        if (contentEditableRef.current) {
                          contentEditableRef.current.innerHTML = editedContent;
                        }
                      }, 100);
                    }
                  }} 
                  className="w-full"
                >
                  <TabsList className="grid w-full grid-cols-2 bg-[#1a1c1b] border border-[#3f4816]">
                    <TabsTrigger value="visual" className="data-[state=active]:bg-[#3f4816] data-[state=active]:text-[#d9fb06]">
                      <FileText className="mr-2 h-4 w-4" />
                      Visual Editor
                    </TabsTrigger>
                    <TabsTrigger value="html" className="data-[state=active]:bg-[#3f4816] data-[state=active]:text-[#d9fb06]">
                      <Code className="mr-2 h-4 w-4" />
                      HTML View
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="visual" className="mt-4">
                    <div className="border border-[#3f4816] rounded-lg overflow-hidden bg-white">
                      <div className="bg-[#1a1c1b] p-2 border-b border-[#3f4816]">
                        <p className="text-xs text-[#888680]">Edit the email content below. Only the main content section is editable.</p>
                      </div>
                      {/* Toolbar */}
                      <div className="bg-[#f5f5f5] border-b border-[#e5e5e5] p-2 flex gap-2 flex-wrap">
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => document.execCommand('bold', false, null)}
                          className="h-8 px-2 text-[#1a1c1b] hover:bg-[#e5e5e5]"
                        >
                          <strong>B</strong>
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => document.execCommand('italic', false, null)}
                          className="h-8 px-2 text-[#1a1c1b] hover:bg-[#e5e5e5]"
                        >
                          <em>I</em>
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => document.execCommand('underline', false, null)}
                          className="h-8 px-2 text-[#1a1c1b] hover:bg-[#e5e5e5]"
                        >
                          <u>U</u>
                        </Button>
                        <div className="w-px bg-[#e5e5e5] mx-1" />
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => document.execCommand('formatBlock', false, '<h1>')}
                          className="h-8 px-2 text-[#1a1c1b] hover:bg-[#e5e5e5]"
                        >
                          H1
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => document.execCommand('formatBlock', false, '<h2>')}
                          className="h-8 px-2 text-[#1a1c1b] hover:bg-[#e5e5e5]"
                        >
                          H2
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => document.execCommand('formatBlock', false, '<h3>')}
                          className="h-8 px-2 text-[#1a1c1b] hover:bg-[#e5e5e5]"
                        >
                          H3
                        </Button>
                        <div className="w-px bg-[#e5e5e5] mx-1" />
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => document.execCommand('insertUnorderedList', false, null)}
                          className="h-8 px-2 text-[#1a1c1b] hover:bg-[#e5e5e5]"
                        >
                          •
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => document.execCommand('insertOrderedList', false, null)}
                          className="h-8 px-2 text-[#1a1c1b] hover:bg-[#e5e5e5]"
                        >
                          1.
                        </Button>
                        <div className="w-px bg-[#e5e5e5] mx-1" />
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            const url = prompt('Enter URL:');
                            if (url) document.execCommand('createLink', false, url);
                          }}
                          className="h-8 px-2 text-[#1a1c1b] hover:bg-[#e5e5e5]"
                        >
                          🔗
                        </Button>
                      </div>
                      {/* ContentEditable Editor */}
                      <div 
                        ref={contentEditableRef}
                        contentEditable
                        suppressContentEditableWarning
                        onInput={(e) => {
                          const newContent = e.currentTarget.innerHTML;
                          if (newContent !== editedContent) {
                            setEditedContent(newContent);
                          }
                        }}
                        onBlur={(e) => {
                          const newContent = e.currentTarget.innerHTML;
                          if (newContent !== editedContent) {
                            setEditedContent(newContent);
                          }
                        }}
                        className="p-4 min-h-[400px] focus:outline-none"
                        style={{ 
                          color: '#1a1c1b',
                          fontSize: '16px',
                          lineHeight: '1.6'
                        }}
                      />
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="html" className="mt-4">
                    <div className="border border-[#3f4816] rounded-lg overflow-hidden">
                      <div className="bg-[#1a1c1b] p-2 border-b border-[#3f4816]">
                        <p className="text-xs text-[#888680]">Edit the HTML directly. Only edit the main content section.</p>
                      </div>
                      <Textarea
                        value={editedContent}
                        onChange={(e) => setEditedContent(e.target.value)}
                        className="bg-[#1a1c1b] border-[#3f4816] text-white font-mono text-sm min-h-[400px]"
                        style={{ fontFamily: 'monospace', fontSize: '13px' }}
                      />
                    </div>
                  </TabsContent>
                </Tabs>
                
                {/* Live Preview */}
                <div className="mt-4">
                  <h4 className="text-[#d9fb06] font-semibold mb-2">Live Preview</h4>
                  <div className="border border-[#3f4816] rounded-lg overflow-hidden">
                    <iframe
                      srcDoc={(() => {
                        let previewHtml = editedContent 
                          ? reconstructHtml(selectedTemplate.html, editedContent)
                          : selectedTemplate.html;
                        // Replace contact name placeholder in preview
                        if (selectedTemplate.requiresContactName) {
                          if (contactName && contactName.trim()) {
                            previewHtml = previewHtml.replace(/\{\{CONTACT_NAME\}\}/g, contactName.trim());
                          } else {
                            previewHtml = previewHtml.replace(/Hey,\s*\{\{CONTACT_NAME\}\}!/g, 'Hey!');
                            previewHtml = previewHtml.replace(/Hello\s*\{\{CONTACT_NAME\}\},/g, 'Hello,');
                          }
                        }
                        return previewHtml;
                      })()}
                      title="Email Preview"
                      className="w-full h-[500px] bg-white"
                      style={{ border: 'none' }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Campaigns;
